import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Container,
  createTheme,
  CssBaseline,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  ThemeProvider,
  Typography
} from '@mui/material';

// All known services and their corresponding ports:
const SERVICE_OPTIONS = {
  "bingo": 3232,
  "cliqq": 2121,
  "gmail": 6565,
  "telegram": 4343,
  "moveit": 7676,
  "whatsapp": 5454,
  "gigalink": 8787,
  "foodpanda": 9898,
  "shopee": 5151,
  "lazada": 5252,
  "gcash": 5353,
  "playzone": 7171,
  "viber": 7272,
  "joyride": 7373,
  "grab": 7474,
  "enstack": 7575,
  "hinance": 7878,
  "savbra": 7979,
  "hotel101": 2929,
  "facebook": 3939,
  "nike": 8686,
  "zus": 9797
};

const BASE_URL = "https://frek.cc/proxy/38.54.86.24:3893"; // Adjust if needed

// Create a MUI "dark mode" theme
const darkTheme = createTheme({
  palette: { mode: 'dark' },
});

function ServiceMappings() {
  // We'll store the mappings for 32 ports in state.
  // Each element: { port, service_name, service_port }
  const [portData, setPortData] = useState(() =>
    Array.from({ length: 32 }, (_, i) => ({
      port: i + 1,
      service_name: "",
      service_port: ""
    }))
  );

  // A place to log messages
  const [logMessages, setLogMessages] = useState([]);

  // Helper to add a message to our "log"
  const logMessage = (msg, isError = false) => {
    const timestamp = new Date().toLocaleTimeString();
    const prefix = isError ? "ERROR: " : "";
    setLogMessages(prev => [...prev, `[${timestamp}] ${prefix}${msg}`]);
  };

  // Automatically load mappings once on mount
  useEffect(() => {
    loadMappings();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Fetch existing mappings from the backend
  const loadMappings = async () => {
    try {
      const resp = await fetch(`${BASE_URL}/get-service-mappings`);
      if (!resp.ok) {
        throw new Error(`HTTP error (status ${resp.status})`);
      }
      const data = await resp.json();
      logMessage("Loaded current mappings.");

      // Build newPortData from server response
      const newPortData = Array.from({ length: 32 }, (_, i) => {
        const portIndex = i + 1;
        const mapping = data[portIndex];
        if (mapping) {
          return {
            port: portIndex,
            service_name: mapping.service_name || "",
            service_port: mapping.service_port || ""
          };
        } else {
          return {
            port: portIndex,
            service_name: "",
            service_port: ""
          };
        }
      });
      setPortData(newPortData);
    } catch (err) {
      logMessage(`Failed to load mappings: ${err}`, true);
    }
  };

  // Update a single port's mapping
  const updateSingleMapping = async (port) => {
    const row = portData.find(r => r.port === port);
    if (!row) return;

    const payload = {
      updates: [
        {
          port: row.port,
          service_name: row.service_name,
          service_port: Number(row.service_port) || 0
        }
      ]
    };

    try {
      const resp = await fetch(`${BASE_URL}/update-service-mappings`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload)
      });
      if (!resp.ok) {
        throw new Error(`HTTP error (status ${resp.status})`);
      }
      const data = await resp.json();
      logMessage(`Port ${port} updated => ${JSON.stringify(data)}`);
    } catch (err) {
      logMessage(`Failed to update port ${port}: ${err}`, true);
    }
  };

  // Update ALL ports in one request
  const updateAllMappings = async () => {
    const updates = portData.map(row => ({
      port: row.port,
      service_name: row.service_name,
      service_port: Number(row.service_port) || 0
    }));

    const payload = { updates };

    try {
      const resp = await fetch(`${BASE_URL}/update-service-mappings`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload)
      });
      if (!resp.ok) {
        throw new Error(`HTTP error (status ${resp.status})`);
      }
      const data = await resp.json();
      logMessage(`All ports updated => ${JSON.stringify(data)}`);
    } catch (err) {
      logMessage(`Failed to update all ports: ${err}`, true);
    }
  };

  // Handle user selecting a different service from the dropdown
  const handleServiceChange = (port, newServiceName) => {
    setPortData(prev =>
      prev.map(row => {
        if (row.port === port) {
          const newPortVal = SERVICE_OPTIONS[newServiceName] || "";
          return {
            ...row,
            service_name: newServiceName,
            service_port: newPortVal
          };
        }
        return row;
      })
    );
  };

  return (
    <ThemeProvider theme={darkTheme}>
      <CssBaseline />
      <Container maxWidth="md" sx={{ mt: 1, mb: 1 }}>
        <Typography variant="body1" sx={{ fontWeight: 'bold', mb: 1 }}>
          Manage Service Mappings (Ports 1..32)
        </Typography>

        <Box sx={{ mb: 1 }}>
          {/* We removed the "Load Mappings" button;
              only "Update All" remains */}
          <Button
            variant="contained"
            size="small"
            color="secondary"
            onClick={updateAllMappings}
          >
            Update All
          </Button>
        </Box>

        {/* Log area 
        <Paper
          sx={{
            p: 0.5,
            maxHeight: 80,
            overflow: "auto",
            backgroundColor: "#424242", // Slightly different dark gray
            mb: 1
          }}
        >
          {logMessages.map((msg, idx) => (
            <Typography
              key={idx}
              variant="caption"
              sx={{ fontFamily: "monospace", display: "block" }}
            >
              {msg}
            </Typography>
          ))}
        </Paper>
*/}
        <TableContainer component={Paper} sx={{ backgroundColor: "#424242" }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontSize: "0.75rem" }}>Port #</TableCell>
                <TableCell sx={{ fontSize: "0.75rem" }}>Service Name</TableCell>
              {/*  <TableCell sx={{ fontSize: "0.75rem" }}>Service Port</TableCell>  */}
                <TableCell sx={{ fontSize: "0.75rem" }}>Action</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {portData.map((row) => (
                <TableRow key={row.port}>
                  <TableCell sx={{ fontSize: "0.7rem" }}>
                    {row.port}
                  </TableCell>
                  <TableCell>
                    <Select
                      value={row.service_name}
                      onChange={(e) => handleServiceChange(row.port, e.target.value)}
                      size="small"
                      displayEmpty
                      
                      sx={{ minWidth: 0, fontSize: "0.7rem" }}
                    >
                      <MenuItem value="" sx={{ fontSize: "0.7rem" }}>
                        <em>-- Service --</em>
                      </MenuItem>
                      {Object.keys(SERVICE_OPTIONS).map(svcName => (
                        <MenuItem
                          key={svcName}
                          value={svcName}
                          sx={{ fontSize: "0.7rem" }}
                        >
                          {svcName}
                        </MenuItem>
                      ))}
                    </Select>
                  </TableCell>
                  {/*  <TableCell>
                    <TextField
                      type="number"
                      value={row.service_port}
                      size="small"
                      disabled
                      sx={{ width: 60, fontSize: "0.7rem" }}
                      inputProps={{ style: { fontSize: "0.7rem" } }}
                    />
                  </TableCell> */}
                  <TableCell>
                    <Button
                      variant="outlined"
                      size="small"
                      sx={{ fontSize: "0.6rem" }}
                      onClick={() => updateSingleMapping(row.port)}
                    >
                      Update
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Container>
    </ThemeProvider>
  );
}

export default ServiceMappings;




